import React, { useEffect, useRef } from 'react'
import { useStripeCheckout } from '../../hooks/useStripeCheckout'
import {
    EmbeddedCheckout,
    EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js'
import { useSearchParams } from 'react-router-dom'
import { RootState } from '../../store'
import { useSelector } from 'react-redux'
import { Box, useTheme, useMediaQuery, CircularProgress, Typography } from '@mui/material'
import { stripePromise } from '../../helpers/stripe'

export const StripeCheckout = () => {
    const { clientSecret, createPaymentIntent, isLoading } = useStripeCheckout()
    const selectedLease = useSelector((state: RootState) => state.lease.selectedLease)
    const bills = useSelector((state: RootState) => state.billing.bills)
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const [searchParams] = useSearchParams()
    const hasCreatedPaymentIntent = useRef(false)

    useEffect(() => {
        if (hasCreatedPaymentIntent.current) return

        const payAll = searchParams.get('payAll')
        const billId = searchParams.get('billId')

        if (payAll === 'true') {
            // Pay all unpaid bills
            const unpaidBillIds = bills
                ?.filter(bill => !bill.is_paid && bill.balance_due > 0)
                .map(bill => bill.id) || []
            createPaymentIntent(Number(selectedLease?.id), unpaidBillIds)
            hasCreatedPaymentIntent.current = true
        } else if (billId) {
            // Pay specific bill
            createPaymentIntent(Number(selectedLease?.id), [Number(billId)])
            hasCreatedPaymentIntent.current = true
        }
    }, [searchParams, bills, selectedLease])

    const options = { clientSecret }

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
            }}
        >
            {isLoading || !clientSecret ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                    }}
                >
                    <CircularProgress size={60} sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="h6" color="white">
                        Loading payment form...
                    </Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: isMobile ? 'calc(100% - 30px)' : '800px',
                        backgroundColor: 'white',
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                        border: `1px solid ${theme.palette.primary.light}`,
                        '& .stripe-embedded-checkout': {
                            width: '100%',
                        },
                        '& iframe': {
                            width: '100% !important',
                            border: 'none',
                            borderRadius: 3,
                        },
                    }}
                >
                    <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
                        <EmbeddedCheckout />
                    </EmbeddedCheckoutProvider>
                </Box>
            )}
        </Box>
    )
}
